/** @format */

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { getGa4PageContext } from 'src/analytics/ga4PageContext';
import { getGa4UserContext } from 'src/analytics/ga4UserContext';
import { trackGa4 } from 'src/analytics/trackGa4';
import type {
  Ga4BaseParams,
  Ga4ComponentInteractionParams,
  Ga4DocumentDownloadParams,
  Ga4SearchParams,
  Ga4SearchNoResultsParams,
  Ga4SearchResultClickParams,
  Ga4WidgetClickParams
} from 'src/analytics/ga4Parameters';
import useAuth from 'src/hooks/useAuth';

function createTracker(eventKey: string, pageContext: Ga4BaseParams, userContext: ReturnType<typeof getGa4UserContext>) {
  return (params: Record<string, unknown> = {}) => trackGa4(eventKey, params, { pageContext, userContext });
}

export default function useGa4Analytics() {
  const { pathname, search } = useLocation();
  const { isAuthenticated, uuid } = useAuth();

  const pageContext = useMemo(() => getGa4PageContext(pathname, search), [pathname, search]);
  const userContext = useMemo(() => getGa4UserContext({ isAuthenticated, uuid }), [isAuthenticated, uuid]);

  return useMemo(
    () => ({
      trackPageView: createTracker('page_view', pageContext, userContext) as (params?: Ga4BaseParams) => void,
      trackLogin: createTracker('login', pageContext, userContext) as (params?: Ga4BaseParams) => void,
      trackLogout: createTracker('logout', pageContext, userContext) as (params?: Ga4BaseParams) => void,
      trackRegistrationStart: createTracker('registration_start', pageContext, userContext) as (
        params?: Ga4BaseParams
      ) => void,
      trackRegistrationComplete: createTracker('registration_complete', pageContext, userContext) as (
        params?: Ga4BaseParams
      ) => void,
      trackWidgetClick: createTracker('widget_click', pageContext, userContext) as (
        params?: Ga4BaseParams & Ga4WidgetClickParams
      ) => void,
      trackComponentInteraction: createTracker('component_interaction', pageContext, userContext) as (
        params?: Ga4BaseParams & Ga4ComponentInteractionParams
      ) => void,
      trackDocumentDownload: createTracker('document_download', pageContext, userContext) as (
        params?: Ga4BaseParams & Ga4DocumentDownloadParams
      ) => void,
      trackSearch: createTracker('search', pageContext, userContext) as (
        params?: Ga4BaseParams & Ga4SearchParams
      ) => void,
      trackSearchResultClick: createTracker('search_result_click', pageContext, userContext) as (
        params?: Ga4BaseParams & Ga4SearchResultClickParams
      ) => void,
      trackSearchNoResults: createTracker('search_no_results', pageContext, userContext) as (
        params?: Ga4BaseParams & Ga4SearchNoResultsParams
      ) => void
    }),
    [pageContext, userContext]
  );
}
